import { useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { AttachmentDropzone } from '../../../../components/chat/AttachmentDropzone'
import { draftsToPendingAttachments } from '../../../../hooks/useAttachmentUpload'
import { useSystemIssueAttachmentUpload } from '../../hooks/useSystemIssueAttachmentUpload'
import { useCreateIssue } from '../../hooks/useSystemIssues'
import { useMentionCandidates } from '../../hooks/useSystemIssueMentions'
import { useProcessSidebarInfo } from '../../../occurrences/hooks/useProcessList'
import { useReportCompany } from '../../hooks/useReportCompany'
import { useIssueProcessLink } from '../../hooks/useIssueProcessLink'
import { CompanySelect } from '../shared/CompanySelect'
import { BackButton } from '@/components/ui/BackButton'
import { filesFromClipboard } from '@/utils/clipboard'
import { MentionableTextarea } from '../../../occurrences/components/chat/MentionableTextarea'
import { PRIORITY_LABELS } from '../shared/IssueRow'
import { ProcessLinkField } from './ProcessLinkField'
import { ReportExamples } from './ReportExamples'
import { SYSTEM_ISSUES_NOT_ENABLED_MESSAGE } from '../../utils/access'
import type { SystemIssue, SystemIssuePriority } from '../../types/system-issues'

interface NewIssueFormProps {
  onCreated: (issue: SystemIssue) => void
  /**
   * Processo detectado pela página atual (widget abriu enquanto o usuário
   * estava em `/processo/:processId`) — entra na lista quando a pessoa
   * responde "sim" à pergunta dos processos; nunca responde por ela.
   */
  relatedProcessId?: number | null
  /**
   * Já começa com "sim" (e o processo da página) — quem chegou aqui já disse
   * que é do processo (ex: no modal pequeno de Ocorrências do processo, antes
   * de abrir este formulário completo em outra aba).
   */
  initialLinkProcess?: boolean
  /**
   * Vínculo obrigatório com `relatedProcessId`: usado pela aba HUB dentro de
   * Ocorrências, onde o problema é SEMPRE do processo em que está sendo
   * aberto. A resposta fica "sim" e esse processo não sai da lista.
   */
  lockProcessLink?: boolean
  /** Mostra o "Voltar" no topo do formulário (tela cheia de Atendimento
   * HUB). A aba HUB do processo já tem o próprio "Cancelar". */
  onBack?: () => void
}

const FIELD_CLASS =
  'w-full rounded-md border border-border-subtle bg-surface px-3 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40'
const LABEL_CLASS = 'mb-1 block text-xs font-medium text-body/80'

export function NewIssueForm({
  onCreated,
  relatedProcessId,
  initialLinkProcess = false,
  lockProcessLink = false,
  onBack,
}: NewIssueFormProps) {
  const { hasRankIn, capabilities } = useAuth()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<SystemIssuePriority>('medium')
  const processLink = useIssueProcessLink({
    contextProcessId: relatedProcessId ?? null,
    locked: lockProcessLink,
    initialYes: initialLinkProcess,
    // Busca de processo só para quem tem Ocorrências (o endpoint é de lá).
    canSearch: capabilities?.canAccessOccurrences === true,
  })
  // Processo vindo do contexto (página do processo / aba HUB): número para o
  // rótulo e empresa para o seletor seguir.
  const { data: contextProcess } = useProcessSidebarInfo(relatedProcessId ?? null)
  const report = useReportCompany(
    (relatedProcessId
      ? contextProcess?.company_id
      : processLink.firstPickedCompanyId) ?? null,
  )
  // Mesma restrição do backend (auth/authz/priority-gate.ts#canUseHighOrUrgentPriority):
  // Coordenador+ NA empresa escolhida (rank é do vínculo; sem empresa, só
  // medium/low) — replicada aqui só pra já não oferecer no dropdown uma opção
  // que o servidor recusaria, evitando um 403 confuso pro usuário.
  const canEscalatePriority = hasRankIn(report.companyId, 'coordenador')
  const availablePriorities: SystemIssuePriority[] = canEscalatePriority
    ? ['urgent', 'high', 'medium', 'low']
    : ['medium', 'low']
  // Trocar de empresa pode tirar a opção já escolhida: volta pra `medium`.
  const effectivePriority = availablePriorities.includes(priority) ? priority : 'medium'
  // Colegas da empresa escolhida pro `@` da descrição — quem é mencionado é
  // marcado na issue pelo backend (mesmo parser de Ocorrências).
  const { data: mentionCandidates = [] } = useMentionCandidates(report.companyId)
  const createIssue = useCreateIssue()
  const {
    drafts,
    addFiles,
    removeDraft,
    reset: resetAttachments,
    isUploading,
    hasErrors,
  } = useSystemIssueAttachmentUpload()

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    processLink.valid &&
    !isUploading &&
    !hasErrors &&
    !createIssue.isLoading

  // Print colado com Ctrl+V na descrição vira anexo — mesmo caminho do
  // clipe (filtro de imagem, limite de tamanho/quantidade em
  // `useAttachmentUpload`). Texto colado segue normal.
  const handleDescriptionPaste = (
    e: React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    const files = filesFromClipboard(e.clipboardData)
    if (files.length === 0) return
    e.preventDefault()
    addFiles(files)
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    createIssue.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        priority: effectivePriority,
        related_process_ids:
          processLink.linkedIds.length > 0 ? processLink.linkedIds : undefined,
        attachments: draftsToPendingAttachments(drafts),
        company_id: report.companyId ?? undefined,
      },
      {
        onSuccess: ({ issue }) => {
          setTitle('')
          setDescription('')
          setPriority('medium')
          processLink.reset()
          resetAttachments()
          onCreated(issue)
        },
        // Sem isto a falha era silenciosa: o botão voltava para "Enviar" e
        // nada acontecia. Rascunho fica intacto para tentar de novo.
        onError: (err) => {
          const status = (err as { response?: { status?: number } })
            .response?.status
          toast.error(
            status === 403
              ? SYSTEM_ISSUES_NOT_ENABLED_MESSAGE
              : err instanceof Error
                ? err.message
                : 'Não foi possível enviar o chamado. Tente novamente.',
          )
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {onBack && (
        <div>
          <BackButton onClick={onBack} />
        </div>
      )}

      <div className="flex flex-wrap items-start gap-3">
        {/* flex-basis em vez de `sm:w-*`: o form também vive no widget
            flutuante (28rem), onde o breakpoint da JANELA não diz nada
            sobre o espaço disponível. */}
        <div className="min-w-0 flex-[2_1_16rem]">
          <label htmlFor="new-issue-title" className={LABEL_CLASS}>
            Título
          </label>
          <input
            id="new-issue-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="Resuma o problema em poucas palavras"
            className={FIELD_CLASS}
          />
        </div>
        {report.showSelector && (
          <div className="flex-[0_1_14rem]">
            <label htmlFor="new-issue-company" className={LABEL_CLASS}>
              Empresa
            </label>
            <CompanySelect
              id="new-issue-company"
              options={report.options}
              value={report.companyId}
              onChange={report.setCompanyId}
              describedBy="new-issue-company-hint"
              className={FIELD_CLASS}
            />
            {/* Quem tem várias empresas às vezes abria o chamado na errada. */}
            <p
              id="new-issue-company-hint"
              className="mt-1 flex items-start gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
            >
              <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
              Certifique-se de estar escolhendo a empresa correta.
            </p>
          </div>
        )}
        <div className="flex-[0_0_9rem]">
          <label htmlFor="new-issue-priority" className={LABEL_CLASS}>
            Prioridade
          </label>
          <select
            id="new-issue-priority"
            value={effectivePriority}
            onChange={(e) => setPriority(e.target.value as SystemIssuePriority)}
            className={FIELD_CLASS}
          >
            {availablePriorities.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ProcessLinkField
        link={processLink}
        contextProcess={
          relatedProcessId
            ? {
                id: relatedProcessId,
                label: contextProcess?.process_number ?? `#${relatedProcessId}`,
                company: contextProcess?.company ?? null,
              }
            : null
        }
      />

      <div>
        <label htmlFor="new-issue-description" className={LABEL_CLASS}>
          Descrição
        </label>
        <MentionableTextarea
          id="new-issue-description"
          candidates={mentionCandidates}
          value={description}
          onChange={setDescription}
          onPaste={handleDescriptionPaste}
          rows={5}
          placeholder="O que aconteceu? O que você esperava que acontecesse? Use @ para marcar um colega da sua empresa e Ctrl+V para colar um print."
          className="w-full resize-none rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40"
        />
      </div>

      <AttachmentDropzone
        drafts={drafts}
        onAddFiles={addFiles}
        onRemove={removeDraft}
        accept="image/*"
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
      >
        {createIssue.isLoading ? 'Enviando…' : 'Enviar'}
      </button>

      <ReportExamples onPick={setTitle} />
    </div>
  )
}
